import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Button, Dialog, DialogActions, Typography, Box, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';

import { ShareType } from 'api';
import { Loaders } from 'components';
import { Share } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  root: {
    '@global': {
      '.flatpickr-input': {
        display: 'none',
      },
      '.flatpickr-calendar': {
        fontFamily: theme.typography.fontFamily,
        borderRadius: 0,
        boxShadow: 'none',
        '&.inline': {
          top: 0,
        },
      },
    },
  },
  button: {
    borderRadius: theme.spacing(3),
  },
}));

const ScheduleShare: FC<Props> = ({ open, onClose }) => {
  const classes = useStyles();
  const { values, setFieldValue, submitForm, setSubmitting, isSubmitting } =
    useFormikContext<Share>();
  const [time, setTime] = useState(moment().add('1', 'hour').toDate());
  const { t } = useTranslation();

  const options = {
    minDate: moment().format(),
    inline: true,
    enableTime: true,
  };

  function handleScheduleChange(nextTime: Date[]) {
    setTime(nextTime[0]);
  }

  async function handleScheduleShare() {
    values.items.forEach((item, index) => {
      setFieldValue(`items[${index}].share_type`, ShareType.Scheduled);
      setFieldValue(`items[${index}].share_at`, moment(time).unix());
    });

    setSubmitting(true);
    await submitForm();
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding={2} marginBottom={-2}>
        <Typography variant="h6">{t('sharing:scheduleYourShare')}</Typography>
        <Typography color="textSecondary">{t('sharing:chooseTime')}</Typography>
        <Box marginTop={2} marginBottom={1}>
          <Divider />
        </Box>
        <div className={classes.root}>
          <Flatpickr value={time} onChange={handleScheduleChange} options={options} />
        </div>
        <Divider />
      </Box>
      <DialogActions>
        <Button onClick={onClose} size="large">
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleScheduleShare}
          className={classes.button}
          color="primary"
          variant="contained"
          size="large"
        >
          {isSubmitting ? <Loaders.Standard height={26} /> : t('sharing:scheduleShare')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleShare;
